import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { Lock } from 'lucide-react';
import { GeneralTab } from './tabs/GeneralTab';
import { AppearanceTab } from './tabs/AppearanceTab';
import { MembersTab } from './tabs/MembersTab';
import { RolesPermissionsTab } from './tabs/RolesPermissionsTab';
import { IntegrationsTab } from './tabs/IntegrationsTab';

import { Workspace } from '@/context/WorkspaceContext';

interface WorkspaceEditorProps {
  workspace: Workspace;
  isGlobalDarkMode: boolean;
  activeTab: 'general' | 'appearance' | 'members' | 'roles-permissions' | 'integrations';
  onTabChange?: (tab: 'general' | 'appearance' | 'members' | 'roles-permissions' | 'integrations') => void;
}

export const WorkspaceEditor: React.FC<WorkspaceEditorProps> = ({
  workspace,
  isGlobalDarkMode,
  activeTab
}) => {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState<'owner' | 'admin' | 'manager' | 'member' | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user || !workspace) return;

      try {
        const { data, error } = await supabase
          .from('workspace_members')
          .select('role')
          .eq('workspace_id', workspace.id)
          .eq('user_id', user.id)
          .eq('status', 'active')
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        setUserRole(data?.role || null);
      } catch (error) {
        console.error('Error fetching user role:', error);
        setUserRole(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [user, workspace]);

  if (loading) {
    return null;
  }

  const isMember = userRole === 'member';

  if (isMember && activeTab !== 'members') {
    return (
      <div className="flex flex-col items-center justify-center py-12 rounded-lg border bg-card border-border">
        <div className="flex items-center justify-center w-16 h-16 rounded-full mb-4 bg-muted">
          <Lock className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-xl font-semibold mb-2 text-foreground">
          Access Restricted
        </h3>
        <p className="text-muted-foreground">
          Members can only access the Members tab.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Your current role: <span className="font-medium capitalize">{userRole || 'member'}</span>
        </p>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralTab workspace={workspace} isGlobalDarkMode={isGlobalDarkMode} />;
      case 'appearance':
        return <AppearanceTab workspace={workspace} isGlobalDarkMode={isGlobalDarkMode} />;
      case 'members':
        return <MembersTab workspace={workspace} isGlobalDarkMode={isGlobalDarkMode} />;
      case 'roles-permissions':
        return <RolesPermissionsTab workspace={workspace} isGlobalDarkMode={isGlobalDarkMode} />;
      case 'integrations':
        return <IntegrationsTab workspace={workspace} isGlobalDarkMode={isGlobalDarkMode} />;
      default:
        return null;
    }
  };

  return (
    <div className="w-full">
      {renderTabContent()}
    </div>
  );
};

